import { Edit, Insights, Refresh } from '@mui/icons-material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, IconButton, Stack, Tooltip } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useNotification } from '../../../hooks/NotificationContext';
import { isExecutiveManager } from '../../../lib/userUtils';
import { getConfigurations } from '../../../modules/config';
import {
  fetchAllExecutionByIteration,
  getTestCaseExecutionsInTestCycle,
} from '../../../modules/executions';
import { getAssociatedUsers } from '../../../modules/project';
import CustomSelect from '../../../ui/primitives/CustomSelect';
import ActionButton from '../../../ui/primitives/ActionButton';
import TestExecutionAccordionItem from './TestExecutionAccordionItem';
import RunAutomationModal from './automation/RunAutomationModal';
import CreateIterationModal from './iterations/CreateIterationModal';
import IterationInsights from './iterations/IterationInsights';
import { Typography } from '@mui/material';
import SearchBar from '../../../ui/primitives/SearchBar';
import useDebounce from '../../../hooks/useDebounce';

const PAGE_SIZE = 30;

const FILTER_OPTIONS = [
  { id: 'all', name: 'All executions' },
  { id: 'assigned', name: 'Assigned to me' },
  { id: 'archived', name: 'Archived TC executions' },
];

const TestExecutionSection = ({
  activeTab,
  testCycleData,
  hide = false,
  viewMode,
  noOfIterations,
  getIterationsByTestCycleId,
  currentIteration,
  setActiveTab,
}) => {
  const { projectId } = useParams();
  const { addNotification } = useNotification();
  const [executions, setExecutions] = useState([]);
  const [openAccordionKey, setOpenAccordionKey] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [labels, setLabels] = useState([]);
  const [iterationModelMode, setIterationModelMode] = useState('create');
  const [showInsightsModal, setShowInsightsModal] = useState(false);
  const didInitRef = useRef(false);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 400);

  const defaultStatus =
    Array.isArray(statuses) && statuses.length > 0
      ? statuses.find((s) => s.is_default)
      : undefined;

  const statusFilterOptions = useMemo(
    () => [
      { id: 'all', name: 'All statuses' },
      ...statuses.map((s) => ({ id: s.id, name: s.name })),
    ],
    [statuses]
  );

  const hasMoreExecutions = useMemo(() => {
    if (!Array.isArray(executions)) return false;
    if (executions.length === 0) return false;
    return executions.length < totalCount;
  }, [executions, totalCount]);

  const fetchExecutionData = useCallback(
    async (currentPage = 1, replace = false, filter, statusFilter, search = '') => {
      if (!activeTab) return;
      setLoading(true);
      try {
        let response = null;
        if (viewMode === 'iterations') {
          response = await fetchAllExecutionByIteration(
            activeTab,
            currentPage,
            PAGE_SIZE,
            filter,
            statusFilter,
            search
          );
        } else if (viewMode === 'testcases') {
          response = await getTestCaseExecutionsInTestCycle(activeTab, currentPage, PAGE_SIZE);
        }
        if (response?.data) {
          setExecutions((prev) => {
            if (replace || currentPage === 1) return response.data;
            const existingIds = new Set(prev.map((p) => p.id));
            const uniqueNew = response.data.filter((d) => !existingIds.has(d.id));
            return prev.concat(uniqueNew);
          });
          setTotalCount(response.total || 0);
        }
      } catch (err) {
        addNotification('danger', 'Failed to fetch execution details');
      } finally {
        setLoading(false);
      }
    },
    [activeTab, viewMode]
  );

  const fetchAllExecutionsForEdit = useCallback(async () => {
    if (!activeTab) return [];
    try {
      setLoading(true);
      let response = null;
      if (viewMode === 'iterations') {
        response = await fetchAllExecutionByIteration(activeTab, 1, 'all', 'all', 'all', '');
      }
      return response?.data || [];
    } catch (err) {
      addNotification('danger', 'Failed to fetch execution details');
      return [];
    } finally {
      setLoading(false);
    }
  }, [activeTab, viewMode]);

  const handleShowMore = useCallback(() => {
    if (loading || !hasMoreExecutions) return;
    setPage((p) => p + 1);
  }, [loading, hasMoreExecutions]);

  const fetchStatuses = useCallback(async () => {
    try {
      const resp = await getConfigurations('testexecutionstatus', projectId);
      if (resp) setStatuses(resp.data || []);
    } catch (err) {
      console.error('fetchStatuses error', err);
    }
  }, [projectId]);

  const getProjectUsers = useCallback(async () => {
    try {
      const usersResponse = await getAssociatedUsers(projectId);
      if (usersResponse) {
        setUsers((usersResponse || []).map((u) => u?.user).filter(Boolean));
      }
    } catch (err) {
      addNotification('danger', 'Error fetching users');
    }
  }, [projectId]);

  const fetchLabels = useCallback(async () => {
    try {
      const resp = await getConfigurations('labels', projectId);
      if (resp?.data) setLabels(resp.data);
    } catch (err) {
      console.error('fetchLabels error', err);
    }
  }, [projectId]);

  const handleAccordionToggle = useCallback((executionId) => {
    setOpenAccordionKey((prev) => (prev === executionId ? null : executionId));
  }, []);

  const refreshData = async (
    resetPage = true,
    filter = selectedFilter,
    statusFilter = selectedStatusFilter
  ) => {
    const targetPage = resetPage ? 1 : page;
    if (resetPage) {
      setPage(1);
    }
    await fetchExecutionData(targetPage, true, filter, statusFilter, debouncedSearch);
  };

  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    fetchStatuses();
    getProjectUsers();
    fetchLabels();
  }, []);

  useEffect(() => {
    if (!activeTab) return;
    setPage(1);
    setTotalCount(0);
    setExecutions([]);
    setSearchTerm('');
    fetchExecutionData(1, true, selectedFilter, selectedStatusFilter, '');
  }, [selectedFilter, selectedStatusFilter, activeTab]);
  useEffect(() => {
    if (!activeTab) return;
    fetchExecutionData(1, true, selectedFilter, selectedStatusFilter, debouncedSearch);
  }, [debouncedSearch]);

  useEffect(() => {
    if (!activeTab || page === 1) return;
    fetchExecutionData(page, false, selectedFilter, selectedStatusFilter, debouncedSearch);
  }, [page]);

  const handleEditIteration = () => {
    setShowModal(true);
    setIterationModelMode('edit');
  };

  const isCycleCompleted = testCycleData?.completion_date !== null;

  const handleSearch = (value) => {
    setSearchTerm(value);
    setPage(1);
  };

  return (
    <>
      <Box className="execution-section">
        {!(isExecutiveManager() || hide) && (
          <Stack direction="row" justifyContent="flex-end" alignItems="center" sx={{ mb: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <ActionButton
                intent="primary"
                size="medium"
                label="+ New Test Iteration"
                tooltip={
                  isCycleCompleted
                    ? 'Test cycle is already completed'
                    : 'Start new iteration'
                }
                disabled={!defaultStatus || isCycleCompleted}
                onClick={() => setShowModal(true)}
              />

              {activeTab &&
                testCycleData?.type === 'Automation' &&
                testCycleData?.job &&
                viewMode === 'iterations' && (
                  <ActionButton
                    intent="primary"
                    size="medium"
                    label="Run Automation"
                    tooltip={
                      isCycleCompleted
                        ? 'Test cycle is already completed'
                        : 'Run Automation'
                    }
                    disabled={isCycleCompleted}
                    onClick={() => setShowConfirmationModal(true)}
                  />
                )}

              <Tooltip title="Click to refresh the data" arrow>
                <IconButton
                  size="small"
                  onClick={() => refreshData(true)}
                  sx={{ border: '1px solid', borderColor: 'divider' }}
                >
                  <Refresh sx={{ fontSize: 22, color: 'text.secondary' }} />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        )}

        {viewMode === 'iterations' && noOfIterations > 0 && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 3 }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <SearchBar
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                clearSearch={() => handleSearch('')}
                placeholder="Search Test Cases"
                width={300}
              />
              <Box sx={{ width: 160 }}>
                <CustomSelect
                  id="executionFilter"
                  value={selectedFilter}
                  onChange={(val) => setSelectedFilter(val)}
                  options={FILTER_OPTIONS}
                  placeholder="Select filter"
                />
              </Box>
              <Box sx={{ width: 130 }}>
                <CustomSelect
                  id="statusFilter"
                  value={selectedStatusFilter}
                  onChange={(val) => setSelectedStatusFilter(val)}
                  options={statusFilterOptions}
                  placeholder="All statuses"
                />
              </Box>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={1}>
              <Tooltip title="Iteration Insights" arrow>
                <IconButton
                  onClick={() => setShowInsightsModal(true)}
                  className="outlined-icon-btn"
                >
                  <Insights />
                </IconButton>
              </Tooltip>

              {!(isExecutiveManager() || hide) && (
                <Tooltip
                  title={isCycleCompleted ? 'Test cycle is already completed' : 'Edit iteration'}
                  arrow
                >
                  <span>
                    <IconButton
                      onClick={handleEditIteration}
                      disabled={!defaultStatus || isCycleCompleted}
                      className="outlined-icon-btn"
                    >
                      <Edit />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Stack>
          </Stack>
        )}

        {executions?.length > 0 ? (
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {executions.map((execution) => (
                <TestExecutionAccordionItem
                  key={execution.id}
                  executionId={execution.id}
                  isExpanded={openAccordionKey === execution.id}
                  onToggle={handleAccordionToggle}
                  currentExecution={execution}
                  isTestCycleCompleted={isCycleCompleted}
                  statuses={statuses}
                  defaultStatus={defaultStatus}
                  fetchExecutionData={() => refreshData(true)}
                  testcaseData={execution?.association?.testcase}
                  users={users}
                  labels={labels}
                />
              ))}
            </Box>

            {hasMoreExecutions && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <ActionButton
                  intent="secondary"
                  label={loading ? 'Loading More...' : `Show More (${executions.length}/${totalCount})`}
                  disabled={loading}
                  loading={loading}
                  onClick={handleShowMore}
                  sx={{
                    width: 350,
                    height: 45,
                  }}
                />
              </Box>
            )}
          </>
        ) : loading ? (
          <Box className="message-body">
            <Typography variant="body2" color="text.secondary">Finding executions...</Typography>
          </Box>
        ) : searchTerm.trim() ? (
          <Box className="message-body">
            <Typography variant="body2" color="text.secondary">
              No executions match &quot;{searchTerm}&quot;
            </Typography>
          </Box>
        ) : (
          <Box className="message-body">
            <Typography variant="body2" color="text.secondary">
              {activeTab
                ? 'No test executions found'
                : viewMode === 'iterations'
                  ? noOfIterations === 0
                    ? null
                    : 'Select an iteration to view executions' +
                    (testCycleData.type === 'Automation' ? ' or run the automation' : '')
                  : 'Select a test case to view executions'}
            </Typography>
          </Box>
        )}
      </Box>

      <CreateIterationModal
        showModal={showModal}
        setShowModal={setShowModal}
        defaultStatus={defaultStatus}
        testCycleId={testCycleData?.id}
        fetchExecutionData={() => refreshData(true)}
        getIterationsByTestCycleId={getIterationsByTestCycleId}
        projectId={projectId}
        noOfIterations={noOfIterations}
        testCycleCountries={testCycleData?.geographies || []}
        mode={iterationModelMode}
        setMode={setIterationModelMode}
        executions={executions}
        currentIterationData={iterationModelMode === 'edit' ? currentIteration : null}
        onIterationEdited={setActiveTab}
        fetchAllExecutionsForEdit={fetchAllExecutionsForEdit}
      />
      <RunAutomationModal
        show={showConfirmationModal}
        handleClose={() => setShowConfirmationModal(false)}
        testCycleId={testCycleData?.id}
        testCycleData={testCycleData}
        activeTab={activeTab}
        executions={executions}
      />
      <IterationInsights
        showModal={showInsightsModal}
        handleCloseModal={() => setShowInsightsModal(false)}
        iterationId={currentIteration?.id}
      />
    </>
  );
};

export default TestExecutionSection;